import React, { useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useSuperAuth } from "@/contexts/SuperAuthContext";
import { toast } from "sonner";
import { Loader2, Shield } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function SuperLogin() {
  const { user, ready, loginStep1, verifyTotp } = useSuperAuth();
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [mfaToken, setMfaToken] = useState("");
  const [otpauthUri, setOtpauthUri] = useState("");
  const [loading, setLoading] = useState(false);

  if (ready && user) return <Navigate to="/super" replace />;

  const submitCredentials = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await loginStep1(email, password);
      if (result.done) {
        toast.success("Sesión iniciada");
        return;
      }
      setMfaToken(result.mfaToken);
      setOtpauthUri(result.otpauthUri || "");
      setStep(result.setup ? "setup" : "totp");
      if (result.setup) toast.info("Escanea el código QR con Google Authenticator");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyTotp(mfaToken, code);
      toast.success("Autenticación completada");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Código incorrecto");
    } finally {
      setLoading(false);
    }
  };

  const qrUrl = otpauthUri
    ? `${API_BASE}/api/auth/totp/qr?uri=${encodeURIComponent(otpauthUri)}`
    : "";

  return (
    <div className="min-h-screen relative grid place-items-center px-4">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0A] via-[#1a1a2e] to-[#0A0A0A]" />
      <div className="relative glass-strong w-full max-w-md p-10 rounded-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 grid place-items-center bg-[#FFD700] text-black rounded-sm">
            <Shield size={24} />
          </div>
          <div>
            <div className="font-display text-3xl text-white">Super Admin</div>
            <div className="text-xs text-white/40 uppercase tracking-widest">2FA obligatorio</div>
          </div>
        </div>

        {step === "credentials" && (
          <form onSubmit={submitCredentials} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Email</label>
              <input type="email" className="input-dark mt-1" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Contraseña</label>
              <input type="password" className="input-dark mt-1" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <button disabled={loading} className="btn-primary w-full justify-center">
              {loading ? <Loader2 className="animate-spin" size={16} /> : "Continuar"}
            </button>
          </form>
        )}

        {(step === "setup" || step === "totp") && (
          <form onSubmit={submitTotp} className="space-y-4">
            {step === "setup" && qrUrl && (
              <div className="text-center">
                <p className="text-xs text-white/60 mb-3">Escanea con Google Authenticator</p>
                <img src={qrUrl} alt="QR 2FA" className="mx-auto bg-white p-2 rounded-sm max-w-[200px]" />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-widest text-white/60">Código de 6 dígitos</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                className="input-dark mt-1 text-center text-2xl tracking-[0.5em]"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
              />
            </div>
            <button disabled={loading || code.length !== 6} className="btn-primary w-full justify-center">
              {loading ? <Loader2 className="animate-spin" size={16} /> : "Verificar"}
            </button>
            <button type="button" className="text-xs text-white/60 w-full" onClick={() => { setStep("credentials"); setCode(""); }}>
              Volver
            </button>
          </form>
        )}

        <div className="mt-6 space-y-2 text-center text-sm">
          <Link to="/" className="block text-white/60 hover:text-[#FFD700]">
            Ver página web del gimnasio
          </Link>
          <Link to="/admin/login" className="block text-white/60 hover:text-white">
            Panel del gimnasio
          </Link>
        </div>
      </div>
    </div>
  );
}
